import type { ObligationResolution, ObligationStatus } from '../compliance-obligations';

/**
 * The compliance IA is deliberately data-only.  Web presentation may add an
 * icon or a role alias, but it must not invent a sixth group or a second route
 * assignment.  Resolver results are a presentation refinement, never access
 * control; the dashboard continues to apply role, module and manifest gates.
 */
export type ComplianceIaGroupId =
  | 'work-calendar'
  | 'events'
  | 'improvement-risk'
  | 'standards-audit'
  | 'governance-provider';

export type ComplianceIaRoute = {
  href: string;
  label: string;
  /** A curated sidebar leaf; other routes remain generated deep links. */
  l1?: boolean;
  /** Route families make detail, create and print pages belong to one group. */
  patterns: readonly string[];
  obligationIds: readonly string[];
  keywords: readonly string[];
};

export type ComplianceIaGroup = {
  id: ComplianceIaGroupId;
  title: string;
  /** Maximum curated sidebar leaves this group may contribute. */
  maxL1Routes: number;
  routes: readonly ComplianceIaRoute[];
};

const universal = [] as const;

/**
 * Canonical navigation families.  Patterns cover every existing compliance
 * page without pretending that a record-detail URL is a separate nav item.
 */
export const COMPLIANCE_IA_GROUPS: readonly ComplianceIaGroup[] = [
  {
    id: 'work-calendar',
    title: 'Work & calendar',
    maxL1Routes: 1,
    routes: [
      {
        href: '/compliance',
        label: 'Compliance hub',
        patterns: ['/compliance'],
        obligationIds: universal,
        keywords: ['compliance', 'hub'],
      },
      {
        href: '/compliance/work',
        label: 'My compliance work',
        l1: true,
        patterns: ['/compliance/work/**'],
        obligationIds: universal,
        keywords: ['worklist', 'assigned', 'overdue'],
      },
      {
        href: '/compliance/calendar',
        label: 'Compliance calendar',
        patterns: ['/compliance/calendar/**'],
        obligationIds: universal,
        keywords: ['due dates', 'agenda', 'calendar'],
      },
    ],
  },
  {
    id: 'events',
    title: 'Events',
    // Statutory event registers need direct operational access. The ceiling is
    // per group/persona, so six curated event leaves remain below ten.
    maxL1Routes: 10,
    routes: [
      {
        href: '/compliance/incidents',
        label: 'Incidents (SIRS)',
        l1: true,
        patterns: ['/compliance/incidents/**', '/compliance/open-disclosure/**'],
        obligationIds: ['sirs'],
        keywords: ['incident', 'sirs', 'open disclosure'],
      },
      {
        href: '/compliance/complaints',
        label: 'Complaints & feedback',
        patterns: ['/compliance/complaints/**'],
        obligationIds: ['complaints_feedback'],
        keywords: ['complaint', 'feedback', 'acknowledgement'],
      },
      {
        href: '/compliance/restrictive-practices',
        label: 'Restrictive practices',
        patterns: ['/compliance/restrictive-practices/**'],
        obligationIds: ['restrictive_practices'],
        keywords: ['bsp', 'authorisation'],
      },
      {
        href: '/compliance/whs',
        label: 'WHS',
        l1: true,
        patterns: ['/compliance/whs/**', '/compliance/environmental/**'],
        obligationIds: ['whs', 'emergency_planning'],
        keywords: ['workplace health', 'safety', 'hazard'],
      },
      {
        href: '/compliance/ipc',
        label: 'Infection prevention and control',
        l1: true,
        patterns: ['/compliance/ipc/**'],
        obligationIds: ['ipc_lead'],
        keywords: ['infection', 'ipc', 'outbreak'],
      },
      {
        href: '/compliance/data-breach',
        label: 'Privacy & data breach',
        l1: true,
        patterns: ['/compliance/data-breach/**'],
        obligationIds: ['privacy_notifiable_data_breaches'],
        keywords: ['privacy', 'data breach', 'oaic'],
      },
    ],
  },
  {
    id: 'improvement-risk',
    title: 'Improvement & risk',
    maxL1Routes: 1,
    routes: [
      {
        href: '/compliance/continuous-improvement',
        label: 'Continuous improvement',
        l1: true,
        patterns: ['/compliance/continuous-improvement/**'],
        obligationIds: ['quality_standards_1_4'],
        keywords: [
          'plan for continuous improvement',
          'PCI',
          'corrective action',
          'quality improvement',
          'suggestion',
          'pdsa',
          'actions',
        ],
      },
      {
        href: '/compliance/governance/risk-register',
        label: 'Risk register',
        patterns: ['/compliance/governance/risk-register/**'],
        obligationIds: ['quality_standards_1_4'],
        keywords: ['risk', 'register'],
      },
    ],
  },
  {
    id: 'standards-audit',
    title: 'Standards & audit',
    maxL1Routes: 1,
    routes: [
      {
        href: '/compliance/standards',
        label: 'Standards',
        l1: true,
        patterns: ['/compliance/standards/**'],
        obligationIds: [
          'quality_standards_1_4',
          'quality_standard_5_1',
          'quality_standard_5_clinical_care',
          'quality_standard_6',
          'quality_standard_7',
          'quality_indicator_program',
        ],
        keywords: ['quality', 'standards', 'evidence'],
      },
      {
        href: '/compliance/quality-indicators',
        label: 'Quality indicators',
        patterns: ['/compliance/quality-indicators/**', '/compliance/quality/qi-data-entry/**'],
        obligationIds: ['quality_indicator_program'],
        keywords: ['quality', 'indicators', 'qi', 'gpms'],
      },
      {
        href: '/compliance/clinical-governance',
        label: 'Clinical governance',
        patterns: ['/compliance/clinical-governance/**'],
        obligationIds: ['quality_standard_5_1', 'quality_standard_5_clinical_care'],
        keywords: ['clinical governance'],
      },
      {
        href: '/compliance/audit-program',
        label: 'Audit program',
        // Evidence packs (Wave 13) are audit evidence assemblies; they live under Standards & audit.
        patterns: [
          '/compliance/audit-program/**',
          '/compliance/audit-pack/**',
          '/compliance/evidence-packs/**',
        ],
        obligationIds: ['quality_standards_1_4'],
        keywords: ['audit', 'evidence', 'assessment'],
      },
      {
        href: '/compliance/documents',
        label: 'Evidence documents',
        patterns: ['/compliance/documents/**'],
        obligationIds: ['record_keeping'],
        keywords: ['documents', 'evidence', 'library'],
      },
      {
        href: '/compliance/reporting',
        label: 'Compliance reporting',
        patterns: ['/compliance/reporting/**'],
        obligationIds: ['dex', 'quality_indicator_program'],
        keywords: ['reporting', 'dex'],
      },
      {
        href: '/compliance/rn-coverage',
        label: 'RN coverage 24/7',
        patterns: ['/compliance/rn-coverage/**'],
        obligationIds: ['rn_24_7'],
        keywords: ['rn', 'coverage', '24/7'],
      },
      {
        href: '/compliance/sah-evidence',
        label: 'Support at Home evidence',
        patterns: ['/compliance/sah-evidence/**'],
        obligationIds: ['quality_standards_1_4'],
        keywords: ['support at home', 'sah', 'evidence'],
      },
      {
        href: '/compliance/star-ratings',
        label: 'Internal Quality Score',
        patterns: ['/compliance/star-ratings/**'],
        obligationIds: ['quality_indicator_program'],
        keywords: ['internal quality score', 'star ratings', 'residential'],
      },
    ],
  },
  {
    id: 'governance-provider',
    title: 'Governance & provider',
    maxL1Routes: 6,
    routes: [
      {
        href: '/compliance/governance',
        label: 'Governance',
        // Risk-register pages are assigned above to Improvement & risk.
        patterns: [
          '/compliance/governance',
          '/compliance/governance/conflicts/**',
          '/compliance/governance/skills-matrix/**',
          '/compliance/governance/qcab/**',
        ],
        obligationIds: [
          'governing_body_composition',
          'governing_body_clinical_member',
          'cooperative_governance_training',
          'quality_care_advisory_body',
          'consumer_advisory_body_offer',
        ],
        keywords: ['governance', 'board', 'conflicts', 'qcab', 'quality care advisory body'],
      },
      {
        href: '/compliance/responsible-persons',
        label: 'Responsible persons',
        l1: true,
        patterns: ['/compliance/responsible-persons/**'],
        obligationIds: ['change_of_circumstances'],
        keywords: ['responsible person', 'key personnel'],
      },
      {
        href: '/compliance/registration',
        label: 'Registration & obligations',
        l1: true,
        patterns: ['/compliance/registration/**'],
        obligationIds: ['associated_provider_notification', 'change_of_circumstances'],
        keywords: ['registration', 'provider'],
      },
      {
        href: '/people/policies-conduct/governance',
        label: 'Policy register',
        patterns: ['/compliance/policies/**', '/compliance/code-of-conduct/**'],
        obligationIds: ['code_of_conduct_provider', 'code_of_conduct_workers'],
        keywords: ['policies', 'code of conduct'],
      },
      {
        href: '/compliance/whistleblower',
        label: 'Whistleblower',
        l1: true,
        patterns: ['/compliance/whistleblower/**'],
        obligationIds: ['whistleblower'],
        keywords: ['whistleblower', 'protected disclosure'],
      },
      {
        href: '/compliance/audit-trail',
        label: 'Audit trail',
        l1: true,
        patterns: ['/compliance/audit-trail/**', '/compliance/b2g/**'],
        obligationIds: ['record_keeping'],
        keywords: ['audit trail', 'activity'],
      },
      {
        href: '/compliance/prudential',
        label: 'Prudential',
        l1: true,
        patterns: ['/compliance/prudential/**'],
        obligationIds: ['financial_prudential_standards', 'acfr_qfr_reporting'],
        keywords: ['prudential', 'financial'],
      },
      {
        href: '/compliance/prudential/reports',
        label: 'Prudential reports',
        // A deep link only: parent prudential remains the L1 financial hub.
        // Keep this discoverable in Cmd-K while preserving the global IA ceiling.
        l1: false,
        patterns: [],
        obligationIds: ['acfr_qfr_reporting'],
        keywords: ['prudential', 'reports'],
      },
      {
        href: '/compliance/prudential/accommodation-agreements',
        label: 'Accommodation agreements',
        // See prudential reports above; both are intentionally folded for density.
        l1: false,
        patterns: [],
        obligationIds: ['financial_prudential_standards'],
        keywords: ['accommodation', 'agreement', 'prudential'],
      },
      {
        href: '/compliance/capability-framework',
        label: 'Capability framework',
        patterns: [
          '/compliance/capability-framework/**',
          '/compliance/training-delivery/**',
          '/compliance/validity-rules/**',
          '/compliance/workforce/**',
        ],
        obligationIds: ['worker_screening'],
        keywords: ['capability', 'workforce', 'training'],
      },
      {
        href: '/compliance/ndis-overlay',
        label: 'NDIS overlay',
        patterns: ['/compliance/ndis-overlay/**'],
        obligationIds: ['ndis_overlay'],
        keywords: ['ndis'],
      },
      {
        href: '/compliance/record-recovery',
        label: 'Record recovery',
        patterns: ['/compliance/record-recovery/**'],
        obligationIds: ['record_keeping'],
        keywords: ['record recovery', 'reconcile'],
      },
    ],
  },
] as const;

export const COMPLIANCE_IA_ROUTES = COMPLIANCE_IA_GROUPS.flatMap((group) => group.routes);

export type ObligationStatuses =
  | Readonly<Record<string, ObligationStatus | ObligationResolution | undefined>>
  | ReadonlyMap<string, ObligationStatus | ObligationResolution | undefined>
  | readonly ObligationResolution[];

function statusFor(
  statuses: ObligationStatuses,
  obligationId: string,
): ObligationStatus | undefined {
  if (Array.isArray(statuses))
    return statuses.find((item) => item.obligationId === obligationId)?.status;
  if ('get' in statuses) {
    const value = (
      statuses as ReadonlyMap<string, ObligationStatus | ObligationResolution | undefined>
    ).get(obligationId);
    return typeof value === 'string' ? (value as ObligationStatus) : value?.status;
  }
  const value = (
    statuses as Readonly<Record<string, ObligationStatus | ObligationResolution | undefined>>
  )[obligationId];
  return typeof value === 'string' ? (value as ObligationStatus) : value?.status;
}

/** Unknown and provider-mediated applicability remain visible; only affirmative non-applicability hides. */
export function resolveGroupVisibility(
  group: ComplianceIaGroup | ComplianceIaGroupId,
  statuses: ObligationStatuses,
): boolean {
  const resolvedGroup =
    typeof group === 'string'
      ? COMPLIANCE_IA_GROUPS.find((candidate) => candidate.id === group)
      : group;
  if (!resolvedGroup) return true;
  if (resolvedGroup.routes.some((route) => route.obligationIds.length === 0)) return true;
  const ids = [...new Set(resolvedGroup.routes.flatMap((route) => route.obligationIds))];
  if (ids.length === 0) return true;
  return !ids.every((id) => statusFor(statuses, id) === 'DOES_NOT_APPLY');
}

export type LandingEntitlements =
  | Readonly<Record<string, boolean | undefined>>
  | ReadonlySet<string>;
export type LandingVisibility =
  | Readonly<Record<string, boolean | undefined>>
  | ((path: string) => boolean);

function isEntitled(path: string, entitlements: LandingEntitlements): boolean {
  if ('has' in entitlements) return (entitlements as ReadonlySet<string>).has(path);
  return (entitlements as Readonly<Record<string, boolean | undefined>>)[path] !== false;
}

function isVisible(path: string, visibility: LandingVisibility): boolean {
  return typeof visibility === 'function' ? visibility(path) : visibility[path] !== false;
}

/** Select a safe home destination; caller supplies the real gate result for each candidate. */
export function resolveLandingPath(
  role: string | null | undefined,
  entitlements: LandingEntitlements,
  visibility: LandingVisibility,
): string {
  const normalized = role?.toUpperCase() ?? '';
  const destination =
    /^(QUALITY_OFFICER|COMPLIANCE_OFFICER|QUALITY_DIRECTOR|REGION_COMPLIANCE_OFFICER|FACILITY_MANAGER)$/.test(
      normalized,
    )
      ? '/compliance/work'
      : /^(REGISTERED_NURSE|ENROLLED_NURSE|CLINICAL_LEAD|CLINICAL_DIRECTOR|ALLIED_HEALTH)$/.test(
            normalized,
          )
        ? '/clinical'
        : /^(CEO|OWNER|TENANT_ADMIN|SYSTEM_ADMIN|OPERATIONS_DIRECTOR)$/.test(normalized)
          ? '/dashboards/compliance'
          : '/overview';
  return isEntitled(destination, entitlements) && isVisible(destination, visibility)
    ? destination
    : '/overview';
}
